import { useEffect, useRef, type ReactNode } from 'react'
import styles from './Modal.module.css'

export interface ModalProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}

/**
 * Native `<dialog>` — focus trapping, Escape-to-close, and the backdrop all
 * come from the browser, not from a hand-rolled implementation. UI spec §4
 * deliberately keeps approval *out* of a modal ("would break the timeline
 * metaphor") — this primitive exists for the cases that aren't that.
 */
export function Modal({ open, title, onClose, children }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      className={styles['dialog']}
      aria-labelledby="modal-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <h2 id="modal-title" className={styles['title']}>
        {title}
      </h2>
      {children}
    </dialog>
  )
}
